import { useEffect, useState } from "react";
import { useMyProviderDataClient } from "../../../../context/ApiContext";

export interface DisplayFlags {
  EmployeeInfo: boolean;
  SessionNoteAvg: boolean;
  SessionNoteGraph: boolean;
  BIP: boolean;
  KPI: boolean;
  Shoutout: boolean;
  ClinicalExcellence: boolean;
  BillableGraph: boolean;
  BillableTable: boolean;
  WeeklyHours: boolean;
  OtherHours: boolean;
  Timeline: boolean;
  NotificationHistory: boolean;
  LOA: boolean;
  RAS: boolean;
  InsuranceEligibility: boolean;
  Benefits: boolean;
  Rates: boolean;
  SignOnTracker: boolean;
  DocumentCompliance: boolean;
  TotalHoursRequired: boolean;
  QuarterlyPerformance: boolean;
}

export const useProviderLayout = (userInfo?: any) => {
  const client = useMyProviderDataClient();

  const [display, setDisplay] = useState<DisplayFlags>({
    EmployeeInfo: false,
    SessionNoteAvg: false,
    SessionNoteGraph: false,
    BIP: false,
    KPI: false,
    Shoutout: false,
    ClinicalExcellence: false,
    BillableGraph: false,
    BillableTable: false,
    WeeklyHours: false,
    OtherHours: false,
    Timeline: false,
    NotificationHistory: false,
    LOA: false,
    RAS: false,
    InsuranceEligibility: false,
    Benefits: false,
    Rates: false,
    SignOnTracker: false,
    DocumentCompliance: false,
    TotalHoursRequired: false,
    QuarterlyPerformance: false
  });

  const [info, setInfo] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userInfo) return;

    const load = async () => {
      try {
        setLoading(true);

        const res = await client.get("/EmployeeInformation/ProviderHealth");
        const items = res.data;

        const newDisplay: DisplayFlags = {
          EmployeeInfo: false,
          SessionNoteAvg: false,
          SessionNoteGraph: false,
          BIP: false,
          KPI: false,
          Shoutout: false,
          ClinicalExcellence: false,
          BillableGraph: false,
          BillableTable: false,
          WeeklyHours: false,
          OtherHours: false,
          Timeline: false,
          NotificationHistory: false,
          LOA: false,
          RAS: false,
          InsuranceEligibility: false,
          Benefits: false,
          Rates: false,
          SignOnTracker: false,
          DocumentCompliance: false,
          TotalHoursRequired: false,
          QuarterlyPerformance: false
        };
        const newInfo: Record<string, string> = {};

        items.forEach((item: any) => {
          let flag = true;

          if (
            item.applicableEmployeeStatus?.length &&
            !item.applicableEmployeeStatus.includes(userInfo.EmpStatus)
          ) {
            flag = false;
          }

          if (
            item.applicableRegions?.length &&
            !item.applicableRegions.includes(userInfo.Region)
          ) {
            flag = false;
          }

          if (
            item.applicableRoles?.length &&
            !item.applicableRoles.includes(userInfo.Role)
          ) {
            flag = false;
          }

          const key = mapTitleToKey(item.title);

          if (key) {
            newDisplay[key] = flag;
            newInfo[key] = item.informationalText;
          }
        });

        setDisplay(newDisplay);
        setInfo(newInfo);
      } catch (err) {
        console.error("Layout load failed", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userInfo, client]);

  return { display, info, loading };
};

const mapTitleToKey = (title: string): keyof DisplayFlags | null => {
  const mapping: Record<string, keyof DisplayFlags> = {
    "Employee Information": "EmployeeInfo",
    "Session Note Latency Total Average": "SessionNoteAvg",
    "Session Note Graph": "SessionNoteGraph",
    "BIP Data": "BIP",
    "KPI Data": "KPI",
    "Shoutouts": "Shoutout",
    "Clinical Excellence": "ClinicalExcellence",
    "Billable Requirements Graph": "BillableGraph",
    "Billable Requirements Table": "BillableTable",
    "Weekly Hours Table": "WeeklyHours",
    "All Other Hours Table": "OtherHours",
    "Timeline": "Timeline",
    "Notification Data": "NotificationHistory",
    "LeaveOfAbsence": "LOA",
    "RAS Data": "RAS",
    "InsuranceEligibility": "InsuranceEligibility",
    "Benefits": "Benefits",
    "Rates": "Rates",
    "SignOnTracker": "SignOnTracker",
    "Document Compliance": "DocumentCompliance",
    "Total Hours Required This Quarter": "TotalHoursRequired",
    "QuarterlyPerformance": "QuarterlyPerformance"
  };

  return mapping[title] ?? null;
};